import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem,
  Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { DeleteOutline, PhotoCameraOutlined, SaveOutlined } from '@mui/icons-material';
import { Layout } from '../../components/common/Layout';
import { UserDto } from '../../models/users/userDto';
import { Gender } from '../../models/enums/gender';
import { usersService } from '../../services/usersService';
import { storageService } from '../../services/storageService';

const emptyUser: UserDto = {
  id: 0, firstName: '', lastName: '', middleName: '', gender: '', birthDate: '',
  email: '', phoneNumber: '', login: '', tenantId: 0,
  createdDate: new Date(), updatedDate: new Date(), createdById: 0, updatedById: 0,
};

const getError = (error: any) => error?.response?.data?.message || error?.message || 'Не удалось сохранить профиль';

export const ProfilePage = () => {
  const [user, setUser] = useState<UserDto>(emptyUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [photo, setPhoto] = useState<File>();
  const [photoPreview, setPhotoPreview] = useState<string>();
  const [removePhoto, setRemovePhoto] = useState(false);

  useEffect(() => {
    usersService.getCurrent()
      .then(({ data }) => {
        if (data.success) {
          setUser(data.data);
          setPhotoPreview(data.data.photoUrl);
        }
      })
      .catch((e) => setError(getError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => () => {
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const update = (field: keyof UserDto) => (event: ChangeEvent<HTMLInputElement>) => {
    setUser((current) => ({ ...current, [field]: event.target.value }));
  };

  const handlePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
  };

  const clearPhoto = () => {
    setPhoto(undefined);
    setPhotoPreview(undefined);
    setRemovePhoto(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    const data = new FormData();
    data.append('FirstName', user.firstName);
    data.append('LastName', user.lastName);
    data.append('MiddleName', user.middleName || '');
    data.append('Email', user.email);
    data.append('PhoneNumber', user.phoneNumber);
    if (user.gender) data.append('Gender', user.gender);
    if (user.birthDate) data.append('BirthDate', user.birthDate.slice(0, 10));
    if (photo) data.append('PhotoFile', photo);
    data.append('RemovePhoto', String(removePhoto));

    try {
      const response = await usersService.updateCurrent(data);
      if (!response.data.success) throw new Error(response.data.message);
      setUser(response.data.data);
      setPhotoPreview(response.data.data.photoUrl);
      setPhoto(undefined);
      setRemovePhoto(false);
      storageService.setUserData(response.data.data);
      window.dispatchEvent(new Event('telemed:user-updated'));
      setSuccess(true);
    } catch (e) {
      setError(getError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Профиль">
      <Paper component="form" onSubmit={handleSubmit} variant="outlined" sx={{ maxWidth: 920, p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6">Личные данные</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>Данные текущего пользователя личного кабинета</Typography>

        {loading ? <Box py={8} textAlign="center"><CircularProgress /></Box> : (
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Avatar src={photoPreview} sx={{ width: 88, height: 88, bgcolor: 'primary.main', fontSize: 28 }}>
                {user.firstName?.[0]}{user.lastName?.[0]}
              </Avatar>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button component="label" variant="outlined" startIcon={<PhotoCameraOutlined />}>
                  Загрузить фото
                  <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} />
                </Button>
                {photoPreview && <Button color="error" startIcon={<DeleteOutline />} onClick={clearPhoto}>Удалить</Button>}
              </Stack>
            </Stack>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }} gap={2}>
              <TextField required label="Фамилия" value={user.lastName} onChange={update('lastName')} />
              <TextField required label="Имя" value={user.firstName} onChange={update('firstName')} />
              <TextField label="Отчество" value={user.middleName || ''} onChange={update('middleName')} />
              <FormControl>
                <InputLabel>Пол</InputLabel>
                <Select label="Пол" value={user.gender || ''} onChange={(e) => setUser({ ...user, gender: e.target.value })}>
                  <MenuItem value=""><em>Не указан</em></MenuItem>
                  <MenuItem value={Gender.Male}>Мужской</MenuItem>
                  <MenuItem value={Gender.Female}>Женский</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Дата рождения" type="date" value={user.birthDate?.slice(0, 10) || ''} onChange={update('birthDate')} InputLabelProps={{ shrink: true }} />
              <TextField required label="Телефон" value={user.phoneNumber} onChange={update('phoneNumber')} />
              <TextField required type="email" label="Электронная почта" value={user.email} onChange={update('email')} sx={{ gridColumn: { sm: '1 / -1' } }} />
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">Профиль сохранён</Alert>}
            <Box><Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />} disabled={saving}>Сохранить</Button></Box>
          </Stack>
        )}
      </Paper>
    </Layout>
  );
};
